import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ResmoMascot from "../components/ResmoMascot";
import ProMoInstructions from "../components/ProMoInstructions";
import Navbar from "../components/Navbar";
import ProgressMeter from "../components/ProgressMeter";
import NewsTicker from "../components/NewsTicker";
import BulkTailorPanel from "../components/BulkTailorPanel";
import { api, formatApiErrorDetail, friendlyAxiosError } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { useBilling } from "../context/BillingContext";
import { useCopy } from "../lib/copy";
import { Wand2, ArrowLeft, FileText, Link as LinkIcon, Upload, Loader, AlertTriangle, Layers, Search } from "lucide-react";

const STAGE_LABEL = {
  pending: "Queued — about to bite",
  analyzing: "Chewing on the job description",
  saving: "Plating your results",
  complete: "Done!",
};

const STAGE_LABEL_PRO = {
  pending: "Queued",
  analyzing: "Analyzing the job description",
  saving: "Saving your results",
  complete: "Done.",
};

// Transient upstream-AI hiccups (overload/timeout/gateway) — reassure, don't alarm.
const isTransientMsg = (m) => {
  const s = (m || "").toLowerCase();
  return [
    "temporarily unavailable", "taking too long", "upstream", "overloaded",
    "timeout", "timed out", "try again in a", "502", "503", "504", "rate limit",
    "brain", "briefly busy",
  ].some((k) => s.includes(k));
};

export default function FeedJob() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPro } = useTheme();
  const { billing } = useBilling();
  const t = useCopy();
  const [mode, setMode] = useState("paste"); // paste | url | file
  const [bulkMode, setBulkMode] = useState(false);
  const [jobDescription, setJobDescription] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jdUrl, setJdUrl] = useState("");
  const [chewing, setChewing] = useState(false);
  const [stage, setStage] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [errorMeta, setErrorMeta] = useState(null); // {budget?: true}
  const [hasResume, setHasResume] = useState(true);
  const [budgetStale, setBudgetStale] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [prefillSource, setPrefillSource] = useState(null); // {source, adzuna_url}
  const [miniMatches, setMiniMatches] = useState([]); // top AI matches for the no-JD nudge
  const [autoCountdown, setAutoCountdown] = useState(null); // null | 3 | 2 | 1 | 0
  const fileRef = useRef(null);
  const analyzeRef = useRef(null);

  // Deep-link from Job Search "Tailor my resume to this job →"
  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf) return;
    if (pf.jobDescription) setJobDescription(pf.jobDescription);
    if (pf.jobTitle) setJobTitle(pf.jobTitle);
    if (pf.companyName) setCompanyName(pf.companyName);
    if (pf.source) setPrefillSource({ source: pf.source, adzuna_url: pf.adzuna_url || "" });
    setMode("paste");
    // Kick the auto-tailor countdown if we have enough JD to analyze
    if (pf.source === "adzuna" && (pf.jobDescription || "").trim().length >= 50) {
      setAutoCountdown(3);
    }
    // Clear location.state so a reload doesn't re-fill on top of edits
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Top AI matches for the "no job description" nudge (cached server-side).
  useEffect(() => {
    api.get("/job-matches")
      .then((r) => setMiniMatches((r.data?.matches || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  const applyMatchToForm = (m) => {
    setJobTitle(m.title || "");
    setCompanyName(m.company || "");
    setJobDescription(m.description || "");
    setPrefillSource({ source: "adzuna", adzuna_url: m.url || "" });
    setMode("paste");
  };

  // Auto-tailor countdown tick. analyzeRef ensures we always call the latest handleAnalyze closure.
  useEffect(() => {
    if (autoCountdown == null) return;
    // Pre-flight: if the user can't actually afford a tailor (and isn't Pro),
    // don't auto-fire — surface a friendly inline notice and cancel the timer
    // so the user can decide whether to buy credits or paste a different JD.
    if (autoCountdown === 3 && billing && !billing.is_pro) {
      const cost = billing?.action_costs?.tailor ?? 5;
      const balance = billing?.credits_balance ?? 0;
      const freeTailorAvailable = billing?.free_tailor_used === false;
      if (!freeTailorAvailable && balance < cost) {
        setAutoCountdown(null);
        setError(`You have ${balance} credit${balance === 1 ? "" : "s"}, but tailoring costs ${cost}. Buy a pack to start tailoring.`);
        setErrorMeta({ paywall: true, needed: cost, balance });
        return;
      }
    }
    if (autoCountdown <= 0) {
      const fn = analyzeRef.current;
      setAutoCountdown(null);
      if (fn) fn();
      return;
    }
    const t = setTimeout(() => setAutoCountdown((n) => (n == null ? null : n - 1)), 1000);
    return () => clearTimeout(t);
  }, [autoCountdown, billing]);

  const cancelAutoTailor = () => setAutoCountdown(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setHasResume(!!data.has_base_resume);
      } catch {
        /* ignore */
      }
      try {
        const { data } = await api.get("/system/budget-status");
        setBudgetStale(!!data.budget_exhausted_recently);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  // Elapsed timer while chewing
  useEffect(() => {
    if (!chewing) return;
    const start = Date.now();
    setElapsed(0);
    const t = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [chewing]);

  const handleFetchUrl = async () => {
    setError("");
    setErrorMeta(null);
    if (!jdUrl.trim()) {
      setError("Paste a job-posting URL first.");
      return;
    }
    setFetching(true);
    try {
      const { data } = await api.post("/job/from-url", { url: jdUrl });
      setJobDescription(data.text);
      if (data.title && !jobTitle) setJobTitle(data.title);
      if (data.company && !companyName) setCompanyName(data.company);
      setMode("paste"); // switch to paste mode so user can review/edit
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setFetching(false);
    }
  };

  const handleFileSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError("");
    setErrorMeta(null);
    setFetching(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/job/from-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobDescription(data.text);
      setMode("paste");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setFetching(false);
    }
  };

  const handleAnalyze = async () => {
    setError("");
    setErrorMeta(null);
    if (jobDescription.trim().length < 50) {
      setError("Job description seems too short. Paste the full posting!");
      return;
    }
    setChewing(true);
    setStage("pending");
    try {
      const { data: jobData } = await api.post("/analyze", {
        job_description: jobDescription,
        company_name: companyName || null,
        job_title: jobTitle || null,
        job_url: prefillSource?.adzuna_url || null,
        job_source: prefillSource?.source || null,
      });
      const jobId = jobData.job_id;

      const pollUntil = Date.now() + 1000 * 60 * 4;
      while (Date.now() < pollUntil) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: status } = await api.get(`/jobs/${jobId}`);
          if (status.stage) setStage(status.stage);
          if (status.status === "complete" && status.analysis_id) {
            window.dispatchEvent(new CustomEvent("resmo:spark", { detail: { kind: "tailor" } }));
            navigate(`/dashboard/${status.analysis_id}`);
            return;
          }
          if (status.status === "error") {
            setChewing(false);
            const errStr = status.error || "Analysis failed. Please try again.";
            if (errStr.includes("LLM_BUDGET_EXHAUSTED") || errStr.toLowerCase().includes("budget")) {
              setError(errStr);
              setErrorMeta({ budget: true });
              setBudgetStale(true);
            } else if (isTransientMsg(errStr)) {
              setError("Resmo's AI is briefly busy — this happens occasionally under load. Nothing's wrong on your end.");
              setErrorMeta({ transient: true });
            } else {
              setError(errStr);
            }
            return;
          }
        } catch {
          /* keep polling on transient errors */
        }
      }
      setChewing(false);
      setError("Resmo's AI is briefly busy — this happens occasionally under load. Nothing's wrong on your end.");
      setErrorMeta({ transient: true });
    } catch (err) {
      setChewing(false);
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      // 402 → paywall (insufficient credits). Backend ships a structured dict.
      if (status === 402 && detail && typeof detail === "object" && detail.error === "insufficient_credits") {
        const needed = detail.credits_needed ?? 5;
        const balance = detail.credits_balance ?? 0;
        setError(`You have ${balance} credit${balance === 1 ? "" : "s"}, but tailoring this resume costs ${needed}.`);
        setErrorMeta({ paywall: true, needed, balance });
        return;
      }
      // Anything else — paywall hits an unstructured 402, 502 gateway timeout, etc.
      const errStr = detail ? formatApiErrorDetail(detail) : friendlyAxiosError(err);
      if (errStr.includes("LLM_BUDGET_EXHAUSTED") || errStr.toLowerCase().includes("budget")) {
        setError(errStr);
        setErrorMeta({ budget: true });
        setBudgetStale(true);
      } else if (status === 503 || status === 502 || status === 504 || isTransientMsg(errStr)) {
        setError("Resmo's AI is briefly busy — this happens occasionally under load. Nothing's wrong on your end.");
        setErrorMeta({ transient: true });
      } else {
        setError(errStr);
      }
    }
  };

  const monsterState = chewing ? "chewing" : "hungry";
  const stageLabel = t(STAGE_LABEL[stage] || "Processing...", STAGE_LABEL_PRO[stage] || "Processing...");

  // Compute a real % progress signal so the user sees forward motion even
  // when an analysis takes longer than the average. We anchor each stage to
  // a baseline percent and let elapsed seconds inch the bar within that
  // stage's allotted band. Caps at 96% until the stage flips to "complete".
  const progressPct = (() => {
    if (stage === "complete") return 100;
    // Per-stage anchor (start of stage) + max ceiling for that stage.
    const stageBand = {
      pending:   { from: 4,  to: 18, perSec: 2.0 },
      analyzing: { from: 18, to: 86, perSec: 1.2 },
      saving:    { from: 86, to: 96, perSec: 1.5 },
      "":        { from: 4,  to: 16, perSec: 1.0 },
    };
    const band = stageBand[stage] || stageBand[""];
    const pct = Math.min(band.to, band.from + elapsed * band.perSec);
    return Math.max(band.from, Math.round(pct));
  })();

  // Keep ref pointed at the latest handleAnalyze closure so the auto-tailor
  // timer fires against the most recent state (jobDescription, jobTitle, ...).
  useEffect(() => {
    analyzeRef.current = handleAnalyze;
  });

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />

      {/* ProMo-only: live news ticker — slim job market pulse strip */}
      {isPro && <NewsTicker />}

      {budgetStale && (
        <div
          className="px-4 md:px-8 py-3"
          style={{ background: "#FEF3C7", borderBottom: "3px solid #0F1B2D" }}
          data-testid="budget-banner"
        >
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} />
              <span className="font-bold">LLM budget recently exhausted.</span>
              <span style={{ color: "#475569" }}>
                {t("Top up your Universal Key to keep Resmo fed.", "Top up your Universal Key to continue analyses.")}
              </span>
            </div>
            <a
              href="https://app.emergent.sh/profile"
              target="_blank"
              rel="noopener noreferrer"
              className="rm-btn rm-btn-primary text-xs px-4 py-2"
              data-testid="topup-link"
            >
              Top up now →
            </a>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/feed-resume")} className="rm-tag" data-testid="back-to-resume-btn">
            <ArrowLeft size={14} /> Resume
          </button>
          <span className="rm-tag" style={{ background: "#FFC145" }}>Step 2 / 2</span>
        </div>

        <h1 className="font-display text-4xl md:text-5xl uppercase mb-2">
          {t(<>Now feed me the <span className="rm-text-orange">job.</span></>, <>Paste the <span className="rm-text-orange">job description.</span></>)}
        </h1>
        <p className="text-base md:text-lg max-w-2xl mb-8" style={{ color: "#475569" }}>
          {t(
            "Paste a description, drop a link, or upload a file — whatever's easiest. Resmo takes it from there.",
            "Paste the text, drop a link, or upload a file. Resmo handles the rest."
          )}
        </p>

        {!hasResume && (
          <div className="rm-card-static p-5 mb-6" style={{ background: "#FEF3C7" }} data-testid="no-resume-warning">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-mono-rm text-sm">⚠ {t("You haven't fed me your resume yet.", "No resume on file yet.")}</div>
              <button onClick={() => navigate("/feed-resume")} className="rm-btn rm-btn-primary text-xs px-4 py-2">
                {t("Feed resume first", "Upload resume first")}
              </button>
            </div>
          </div>
        )}

        {prefillSource?.source === "adzuna" && (
          <div
            className="rm-card-static p-4 mb-6 flex items-start gap-3"
            style={{ background: "#FFFBEB", borderColor: "#FFC145", boxShadow: "4px 4px 0 0 #FF6A00" }}
            data-testid="adzuna-prefill-banner"
          >
            <Wand2 size={18} className="rm-text-orange mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="rm-label mb-1">Pulled from a live Adzuna listing</div>
              <p className="text-sm" style={{ color: "#475569" }}>
                Title, company, and description are pre-filled. Review and edit before tailoring — Adzuna previews can be truncated.
                {prefillSource.adzuna_url && (
                  <>
                    {" "}
                    <a href={prefillSource.adzuna_url} target="_blank" rel="noopener noreferrer"
                       className="rm-text-orange underline" data-testid="adzuna-prefill-source-link">
                      Open the full posting ↗
                    </a>
                  </>
                )}
              </p>
              {autoCountdown != null && !chewing && (
                <div
                  className="mt-3 p-3 flex items-center gap-3 flex-wrap"
                  style={{ background: "#0F1B2D", color: "white", border: "2px solid #FF6A00" }}
                  data-testid="auto-tailor-countdown"
                >
                  <div
                    className="font-display flex-shrink-0 flex items-center justify-center"
                    style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: "#FF6A00", color: "white",
                      fontSize: "1.25rem", lineHeight: 1,
                    }}
                    data-testid="auto-tailor-countdown-number"
                  >
                    {autoCountdown}
                  </div>
                  <div className="flex-1 min-w-[140px] text-sm">
                    Tailoring automatically in <strong>{autoCountdown}s</strong> — cancel if you want to edit first.
                  </div>
                  <button
                    type="button"
                    onClick={cancelAutoTailor}
                    className="rm-btn text-xs"
                    style={{ background: "white", color: "#0F1B2D", border: "2px solid #FFC145",
                             boxShadow: "2px 2px 0 0 #FFC145" }}
                    data-testid="auto-tailor-cancel-btn"
                  >
                    Cancel &amp; edit first
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            {isPro ? (
              <div className="sticky top-28">
                {chewing ? (
                  <ProMoInstructions
                    fullHeight
                    tag="Working"
                    title="Analyzing your fit."
                    intro={stageLabel}
                    steps={[
                      `Progress · ${progressPct}% complete`,
                      "Building skill compatibility matrix.",
                      "Identifying the company's true problem this role solves.",
                      "Generating a tailored resume aligned to the Job Description.",
                    ]}
                    footnote="Don't navigate away — your results will appear here automatically."
                    testId="promo-feed-job-progress"
                  />
                ) : (
                  <ProMoInstructions
                    fullHeight
                    tag="Step 2 of 2"
                    title="Choose your input method."
                    intro="Resmo accepts the job description in three ways. Pick whichever is fastest."
                    steps={[
                      "Paste the full text directly — most reliable.",
                      "Paste a URL — Resmo fetches and extracts the description.",
                      "Upload a PDF, DOCX, or TXT file with the Job Description.",
                      "Add company name & job title (optional, sharpens the analysis).",
                    ]}
                    footnote="Each analysis takes 30-90 seconds. Output: fit assessment, skill matrix, company insight, tailored resume."
                    testId="promo-feed-job-instructions"
                  />
                )}
              </div>
            ) : (
              <div
                className="rm-card-static p-8 flex flex-col items-center justify-center sticky top-28 h-full min-h-[480px]"
                style={{ background: chewing ? "#FFC145" : "#FFF2EC" }}
                data-testid="feed-job-monster-card"
              >
                <ResmoMascot state={monsterState} size={240} flavor="resume" />
                <div className="font-display text-2xl mt-4 text-center uppercase">
                  {chewing ? "Chewing..." : "Toss it in!"}
                </div>
                {chewing ? (
                  <div className="mt-3 w-full" data-testid="progress-block">
                    <div className="font-mono-rm text-xs uppercase tracking-widest text-center" style={{ color: "#475569" }}>
                      {stageLabel}
                    </div>
                    <div className="font-display text-center mt-1"
                         style={{ fontSize: "1.6rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}
                         data-testid="progress-percent">
                      {progressPct}<span style={{ fontSize: "1rem", color: "#475569" }}>%</span>
                    </div>
                    <div
                      className="mt-2 h-3 rounded-full overflow-hidden"
                      style={{ background: "#FFF2EC", border: "2px solid #0F1B2D" }}
                    >
                      <div
                        className="h-full transition-all duration-700"
                        style={{
                          width: `${progressPct}%`,
                          background: "linear-gradient(90deg,#FF6A00,#B45309)",
                        }}
                      />
                    </div>
                    <div className="font-mono-rm text-[10px] uppercase tracking-widest text-center mt-1.5"
                         style={{ color: "#94A3B8" }}>
                      · Resmo is processing · results appear automatically
                    </div>
                  </div>
                ) : (
                  <div className="font-mono-rm text-xs mt-2 uppercase tracking-widest text-center" style={{ color: "#475569" }}>
                    i'm starving
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-7">
            {bulkMode ? (
              <BulkTailorPanel
                isPro={!!billing?.is_pro}
                creditsBalance={billing?.credits_balance ?? 0}
                onClose={() => setBulkMode(false)}
              />
            ) : (
            <div className="rm-card-static p-6 md:p-8 space-y-5">
              {/* Bulk-mode toggle banner */}
              <div
                className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-md"
                style={{ background: "#FFFBEB", border: "2px dashed #FFC145" }}
                data-testid="bulk-mode-banner"
              >
                <div className="flex items-center gap-2 text-sm">
                  <Layers size={16} style={{ color: "#FF6A00" }} />
                  <span>
                    Got <strong>multiple Job Descriptions</strong>? Tailor up to 5 in parallel.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setBulkMode(true)}
                  className="rm-btn rm-btn-primary text-xs px-3 py-1.5"
                  data-testid="open-bulk-mode-btn"
                >
                  <Layers size={13} /> Switch to bulk mode →
                </button>
              </div>
              {/* Mode tabs */}
              <div className="flex gap-2 flex-wrap" data-testid="jd-mode-tabs">
                <button
                  onClick={() => setMode("paste")}
                  className={`rm-btn ${mode === "paste" ? "rm-btn-dark" : "rm-btn-ghost"} text-xs px-4 py-2`}
                  data-testid="jd-mode-paste"
                >
                  <FileText size={14} /> Paste text
                </button>
                <button
                  onClick={() => setMode("url")}
                  className={`rm-btn ${mode === "url" ? "rm-btn-dark" : "rm-btn-ghost"} text-xs px-4 py-2`}
                  data-testid="jd-mode-url"
                >
                  <LinkIcon size={14} /> Paste URL
                </button>
                <button
                  onClick={() => setMode("file")}
                  className={`rm-btn ${mode === "file" ? "rm-btn-dark" : "rm-btn-ghost"} text-xs px-4 py-2`}
                  data-testid="jd-mode-file"
                >
                  <Upload size={14} /> Upload file
                </button>
              </div>

              {/* URL mode */}
              {mode === "url" && (
                <div className="space-y-3" data-testid="jd-url-block">
                  <label className="rm-label block">Job posting URL</label>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      className="rm-input flex-1"
                      style={{ minWidth: 240 }}
                      placeholder="https://jobs.example.com/posting/12345"
                      value={jdUrl}
                      onChange={(e) => setJdUrl(e.target.value)}
                      data-testid="jd-url-input"
                    />
                    <button
                      onClick={handleFetchUrl}
                      disabled={fetching}
                      className="rm-btn rm-btn-primary text-xs px-4 py-2"
                      data-testid="jd-url-fetch-btn"
                    >
                      {fetching ? <Loader size={14} className="animate-spin" /> : <LinkIcon size={14} />}
                      {fetching ? "Fetching..." : "Fetch"}
                    </button>
                  </div>
                  {fetching && (
                    <ProgressMeter compact expectedSeconds={12} label="Fetching the posting" testid="jd-url-progress-meter" />
                  )}
                  <div className="font-mono-rm text-xs" style={{ color: "#475569" }}>
                    Works with most career pages (Greenhouse, Lever, Workday, company sites). LinkedIn / Indeed often hide content behind login — if a fetch comes up empty, copy the description and use the Paste tab.
                  </div>
                </div>
              )}

              {/* File mode */}
              {mode === "file" && (
                <div className="space-y-3" data-testid="jd-file-block">
                  <input
                    type="file"
                    ref={fileRef}
                    accept=".pdf,.docx,.txt"
                    className="hidden"
                    onChange={handleFileSelected}
                    data-testid="jd-file-input"
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={fetching}
                    className="rm-btn rm-btn-primary"
                    data-testid="jd-file-trigger-btn"
                  >
                    <Upload size={14} />
                    {fetching ? "Reading..." : "Choose PDF / DOCX / TXT"}
                  </button>
                  <div className="font-mono-rm text-xs" style={{ color: "#475569" }}>
                    Max 5MB. Text will appear below for review before submitting.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="rm-label block mb-2">Job title (optional)</label>
                  <input
                    type="text"
                    className="rm-input"
                    placeholder="Senior Product Designer"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    data-testid="job-title-input"
                  />
                </div>
                <div>
                  <label className="rm-label block mb-2">Company (optional)</label>
                  <input
                    type="text"
                    className="rm-input"
                    placeholder="Acme Inc."
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    data-testid="company-name-input"
                  />
                </div>
              </div>

              <div>
                <label className="rm-label block mb-2">Job description *</label>
                <textarea
                  className="rm-textarea"
                  style={{ minHeight: 320 }}
                  placeholder="Paste the full job posting here — responsibilities, requirements, perks, the works."
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  data-testid="job-description-textarea"
                />
                <div className="mt-1 font-mono-rm text-xs" style={{ color: "#475569" }}>
                  {jobDescription.length} chars · min 50
                </div>
                {jobDescription.trim().length < 50 && (
                  <div className="mt-3 px-4 py-3 rounded-md"
                       style={{ background: "#FFF7E6", border: "2px dashed #B45309" }}
                       data-testid="no-jd-suggestion">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[220px]">
                        <div className="font-bold text-sm" style={{ color: "#0F1B2D" }}>
                          Don't have a job description yet?
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "#475569" }}>
                          {miniMatches.length > 0
                            ? "Resmo already found live openings that match your resume — tailor one in a click:"
                            : "Let Resmo hunt live openings for your role — pick one and it lands right back here, pre-filled and ready to tailor."}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate("/job-search")}
                        className="rm-btn text-xs flex-shrink-0"
                        style={{ background: "#0F1B2D", color: "white" }}
                        data-testid="no-jd-jobsearch-btn"
                      >
                        <Search size={14} /> {miniMatches.length > 0 ? "More jobs" : "Find me a job"}
                      </button>
                    </div>
                    {miniMatches.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {miniMatches.map((m, i) => (
                          <div key={m.id || i}
                               className="flex items-center gap-3 px-3 py-2 rounded"
                               style={{ background: "white", border: "1px solid #E2C89B" }}
                               data-testid={`no-jd-match-row-${i}`}>
                            <span className="font-mono-rm text-[10px] font-bold px-1.5 py-0.5 rounded text-white flex-shrink-0"
                                  style={{ background: m.match_pct >= 75 ? "#10B981" : m.match_pct >= 60 ? "#FFC145" : "#94A3B8" }}>
                              {m.match_pct}%
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-bold truncate" style={{ color: "#0F1B2D" }}>{m.title}</div>
                              <div className="text-[11px] truncate" style={{ color: "#64748B" }}>
                                {m.company}{m.location ? ` · ${m.location}` : ""}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => applyMatchToForm(m)}
                              className="rm-btn text-[10px] px-2 py-1 flex-shrink-0"
                              style={{ background: "#FF6A00", color: "white" }}
                              title="Fill the form with this job and tailor"
                              data-testid={`no-jd-match-use-${i}`}
                            >
                              <Wand2 size={11} /> Tailor this
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {error && (
                <div
                  className="p-4 rounded-md font-mono-rm text-sm"
                  style={{
                    background: errorMeta?.budget ? "#FEF3C7" : errorMeta?.paywall ? "#FFFBEB" : errorMeta?.transient ? "#EFF6FF" : "#FEF2F2",
                    border: `3px solid ${errorMeta?.budget ? "#F59E0B" : errorMeta?.paywall ? "#FF6A00" : errorMeta?.transient ? "#3B82F6" : "#EF4444"}`,
                    color: errorMeta?.budget ? "#78350F" : errorMeta?.paywall ? "#7C2D12" : errorMeta?.transient ? "#1E3A8A" : "#991B1B",
                  }}
                  data-testid="feed-job-error"
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-bold mb-1">
                        {errorMeta?.budget
                          ? "Universal LLM Key budget exhausted"
                          : errorMeta?.paywall
                            ? "You need more credits to tailor this one"
                            : errorMeta?.transient
                              ? "Resmo's AI is briefly busy"
                              : "Something went wrong"}
                      </div>
                      <div>{error.replace("LLM_BUDGET_EXHAUSTED:", "").trim()}</div>
                      {errorMeta?.transient && (
                        <button
                          onClick={handleAnalyze}
                          disabled={chewing}
                          className="inline-flex items-center gap-2 mt-3 rm-btn text-xs px-3 py-2"
                          style={{ background: "#3B82F6", color: "white" }}
                          data-testid="error-retry-btn"
                        >
                          <Wand2 size={14} /> Try again
                        </button>
                      )}
                      {errorMeta?.budget && (
                        <a
                          href="https://app.emergent.sh/profile"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block mt-3 rm-btn rm-btn-primary text-xs px-3 py-2"
                          data-testid="error-topup-link"
                        >
                          Top up Universal Key →
                        </a>
                      )}
                      {errorMeta?.paywall && (
                        <button
                          onClick={() => navigate("/billing")}
                          className="inline-block mt-3 rm-btn rm-btn-primary text-xs px-3 py-2"
                          data-testid="error-buy-credits-btn"
                        >
                          Buy more credits →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleAnalyze}
                disabled={chewing || !hasResume}
                className="rm-btn rm-btn-primary"
                data-testid="analyze-btn"
              >
                <Wand2 size={18} />
                {chewing ? t("Resmo is thinking...", "Analyzing...") : t("Unleash Resmo", "Analyze")}
              </button>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
